import { useMemo, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import {
  FormControl,
  FormHelperText,
  InputLabel as MuiInputLabel,
  MenuItem,
  Select as MuiSelect,
  SxProps,
} from '@mui/material'

import { DefaultElementProps } from 'common/interfaces'
import InputLabel from 'components/InputLabel'
import { SizingContainer } from 'components/SizingContainer'
import { useIsPhone, useTemplate } from 'utils/hook'
import validationsToRules from 'utils/validationsToRules'

import { Select as SelectProps } from './interface'

interface Option {
  label: string
  value: string
}

const styles: Record<string, SxProps> = {
  inputLabel: {
    whiteSpace: 'normal',
    lineHeight: 1.2,
  },
  selectWithLabelWrap: {
    '& .MuiSelect-select': {
      paddingY: '24px',
    },
  },
}

const isEmptyValue = (val: unknown): boolean => {
  if (val === null) return true
  if (val === undefined) return true
  if (typeof val === 'string' && val === '') return true
  return false
}

function getSafeOptions(
  options: Option[] | undefined,
  currentValue: unknown,
  loading: boolean | undefined
): Option[] {
  const availableOptions = Array.isArray(options) ? options : []

  if (loading) {
    return availableOptions
  }

  if (isEmptyValue(currentValue)) {
    return availableOptions
  }

  const exists = availableOptions.some((o) => String(o.value) === String(currentValue))

  if (exists) {
    return availableOptions
  }

  return [{ label: String(currentValue), value: String(currentValue) }, ...availableOptions]
}

export default function Select({
  name,
  size,
  label,
  options,
  loading,
  onFocus,
  onChange,
  validations,
  defaultValue,
}: SelectProps & DefaultElementProps): JSX.Element {
  const render = useTemplate()

  const { control } = useFormContext()
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue: render(defaultValue ?? ''),
    rules: validationsToRules(validations),
  })

  const [menuOpen, setMenuOpen] = useState(false)

  // When the label is long, assume it will wrap and adjust Select padding accordingly
  const isPhone = useIsPhone()
  const labelMaxLength = isPhone ? 45 : 60

  const isLabelMultiline = label.length >= labelMaxLength

  // Ensure currentValue is always included in the dropdown options
  const safeOptions = useMemo(
    () => getSafeOptions(options, field.value, loading),
    [options, field.value, loading]
  )

  return (
    <SizingContainer className="select-wrap" size={size}>
      <FormControl error={error !== undefined} fullWidth>
        <MuiInputLabel id={name} sx={styles.inputLabel} required={validations?.required?.value}>
          {label}
        </MuiInputLabel>
        <MuiSelect
          {...field}
          onChange={(e) => {
            field.onChange(e)
            onChange?.({ value: e.target.value })
          }}
          onFocus={() => {
            onFocus?.()
          }}
          label={label}
          open={menuOpen}
          onOpen={() => setMenuOpen(true)}
          onClose={() => setMenuOpen(false)}
          labelId={name}
          sx={isLabelMultiline ? styles.selectWithLabelWrap : undefined}
        >
          {loading === true ? (
            <MenuItem key="loading">Loading...</MenuItem>
          ) : (
            safeOptions.map(({ value, label }: Option) => (
              <MenuItem key={value} value={value} sx={{ whiteSpace: 'normal' }}>
                {label}
              </MenuItem>
            ))
          )}
        </MuiSelect>
        {error !== undefined && <FormHelperText>{error.message}</FormHelperText>}
      </FormControl>
    </SizingContainer>
  )
}
